
import React, { useState } from 'react';
import { useSales } from './hooks/useSales';
import { PaymentProcessing } from './PaymentProcessing';

interface CheckoutFlowProps {
  cartItems: any[];
  totals: any;
  customerId: string;
  onComplete: (orderData: any) => void;
  onCancel: () => void;
}

export const CheckoutFlow: React.FC<CheckoutFlowProps> = ({
  cartItems,
  totals,
  customerId,
  onComplete,
  onCancel
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [orderData, setOrderData] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState('advance');
  const { createSalesOrder, getCustomerById } = useSales();

  const customer = getCustomerById(customerId);
  const steps = ['Order Review', 'Payment', 'Confirmation'];

  const handleCreateOrder = async () => {
    try {
      const order = {
        customer_id: customerId,
        customer_name: customer?.name || '',
        customer_phone: customer?.phone || '',
        customer_email: customer?.email || '',
        items: cartItems.map(item => ({
          product_id: item.product.id,
          product_name: item.product.model_name,
          vendor_id: item.product.vendor_id,
          vendor_name: item.product.vendor_name,
          quantity: item.quantity,
          unit_price: item.price,
          total_price: item.total,
          specifications: {
            category: item.product.category,
            metal_type: item.product.metal_type,
            weight_range: item.product.weight_range,
            purity: item.product.purity,
            making_charge: item.product.making_charge,
            delivery_days: item.product.delivery_days
          }
        })),
        totals: {
          subtotal: totals.subtotal,
          making_charges: totals.makingCharges,
          gst: totals.gst,
          total_amount: totals.total,
          advance_amount: totals.advanceAmount,
          balance_amount: totals.remainingAmount,
          advance_percentage: totals.advancePercentage
        },
        payment_method: paymentMethod,
        gold_rate_frozen: totals.advancePercentage >= 85,
        current_gold_rate: 6500, // This should come from gold rates API
        status: 'pending_approval',
        workflow_status: 'booked',
        expected_delivery: Math.max(...cartItems.map(item => item.product.delivery_days)),
        created_by: 'current_user', // This should come from auth
        session_id: `SESSION_${Date.now()}`,
        notes: `Order created with ${totals.advancePercentage}% advance payment`
      };

      const createdOrder = await createSalesOrder.mutateAsync(order);
      setOrderData(createdOrder);
      setCurrentStep(2);
    } catch (error) {
      console.error('Failed to create order:', error);
    }
  };

  const handlePaymentComplete = (paymentData: any) => {
    const completeOrderData = {
      ...orderData,
      payment_details: paymentData,
      status: paymentMethod === 'full_payment' ? 'confirmed' : 'advance_paid',
      workflow_status: 'confirmed'
    };
    
    setOrderData(completeOrderData);
    setCurrentStep(3);
  };

  const handleComplete = () => {
    onComplete(orderData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header with Steps */}
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold text-primary mb-4">Checkout</h2>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  index + 1 <= currentStep 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {index + 1 <= currentStep ? (
                    index + 1 < currentStep ? <i className="ri-check-line"></i> : index + 1
                  ) : index + 1}
                </div>
                <span className={`ml-2 text-sm ${
                  index + 1 <= currentStep ? 'text-primary font-medium' : 'text-gray-500'
                }`}>
                  {step}
                </span>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    index + 1 < currentStep ? 'bg-primary' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Order Review */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Order Review</h3>
                
                {/* Customer Information */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium mb-3">Customer Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <span className="ml-2 font-medium">{customer?.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Phone:</span>
                      <span className="ml-2 font-medium">{customer?.phone}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Email:</span>
                      <span className="ml-2 font-medium">{customer?.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Customer ID:</span>
                      <span className="ml-2 font-medium">{customer?.id}</span>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="space-y-3 mb-6">
                  <h4 className="font-medium">Order Items</h4>
                  {cartItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={item.product.images?.[0] || `https://public.readdy.ai/ai/img_res/45d2b43cea4df421f42a36be8e3c6709.jpg$%7Bitem.product.category%7D%20jewelry%20$%7Bitem.product.metal_type%7D&width=100&height=100&seq=${index}&orientation=squarish`}
                            alt={item.product.model_name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <div className="font-medium">{item.product.model_name}</div>
                          <div className="text-sm text-gray-600">
                            {item.product.category} • {item.product.metal_type} • Qty: {item.quantity}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">₹{item.total.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">₹{item.price.toLocaleString()} each</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Payment Method Selection */}
                <div className="mb-6">
                  <h4 className="font-medium mb-3">Payment Method</h4>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        value="advance"
                        checked={paymentMethod === 'advance'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="text-primary"
                      />
                      <div className="flex-1">
                        <div className="font-medium">Advance Payment ({totals.advancePercentage}%)</div>
                        <div className="text-sm text-gray-600">
                          Pay ₹{totals.advanceAmount.toLocaleString()} now, ₹{totals.remainingAmount.toLocaleString()} on delivery
                        </div>
                      </div>
                      <div className="text-lg font-semibold text-green-600">
                        ₹{totals.advanceAmount.toLocaleString()}
                      </div>
                    </label>
                    
                    <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        value="full_payment"
                        checked={paymentMethod === 'full_payment'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="text-primary"
                      />
                      <div className="flex-1">
                        <div className="font-medium">Full Payment</div>
                        <div className="text-sm text-gray-600">
                          Pay complete amount now, gold rate frozen
                        </div>
                      </div>
                      <div className="text-lg font-semibold text-primary">
                        ₹{totals.total.toLocaleString()}
                      </div>
                    </label>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-3">Order Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>₹{totals.subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Making Charges</span>
                      <span>₹{totals.makingCharges.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>GST (3%)</span>
                      <span>₹{totals.gst.toLocaleString()}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-semibold">
                      <span>Total Amount</span>
                      <span>₹{totals.total.toLocaleString()}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between text-green-600 font-semibold">
                      <span>Amount to Pay Now</span>
                      <span>₹{(paymentMethod === 'full_payment' ? totals.total : totals.advanceAmount).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={onCancel}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-7

                  whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateOrder}
                  className="flex-1 px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 whitespace-nowrap"
                >
                  Create Order &amp; Proceed to Payment
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Payment */}
          {currentStep === 2 && orderData && (
            <PaymentProcessing
              orderId={orderData.id}
              amount={paymentMethod === 'full_payment' ? totals.total : totals.advanceAmount}
              paymentMethod={paymentMethod}
              onPaymentComplete={handlePaymentComplete}
              onCancel={onCancel}
            />
          )}

          {/* Step 3: Confirmation */}
          {currentStep === 3 && orderData && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-green-600 mb-2">Order Confirmed!</h3>
                <p className="text-gray-600">Your order has been successfully placed and payment processed.</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 text-left">
                <h4 className="font-semibold mb-4">Order Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Order ID:</span>
                    <span className="ml-2 font-medium">{orderData.id}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className="ml-2 font-medium text-green-600">Confirmed</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Payment Status:</span>
                    <span className="ml-2 font-medium text-green-600">
                      {paymentMethod === 'full_payment' ? 'Paid in Full' : 'Advance Paid'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Expected Delivery:</span>
                    <span className="ml-2 font-medium">{orderData.expected_delivery} days</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  You will receive SMS and email updates about your order status.
                  The order will now be sent to the vendor for production.
                </p>
                
                {paymentMethod === 'advance' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center text-yellow-800">
                      <i className="ri-information-line mr-2"></i>
                      <span className="font-medium">Balance Payment Required</span>
                    </div>
                    <p className="text-yellow-700 text-sm mt-1">
                      Remaining amount of ₹{totals.remainingAmount.toLocaleString()} will be collected upon delivery.
                    </p>
                  </div>
                )}
              </div>

              <button
                onClick={handleComplete}
                className="px-8 py-3 bg-primary text-white rounded-md hover:bg-primary/90 whitespace-nowrap"
              >
                Complete &amp; Return to Sales
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutFlow;
